/**
 * La sesion en el navegador (D-063).
 *
 * El navegador no tiene ningun token: la sesion es una cookie `HttpOnly` que
 * este codigo no puede leer. Lo unico que sabe es lo que le cuenta la API en
 * `GET /v1/auth/yo` — quien es y como esta su cuenta —, y lo pregunta recien
 * cuando una pantalla lo necesita: quien abre la pagina de una barberia para
 * reservar no hace ni una peticion de mas.
 *
 * El proveedor va arriba del router (`main.tsx`) para que la sesion sea una
 * sola: ingresar en `/ingresar` y llegar al panel no vuelve a preguntar.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { LoadingState } from "../components/states";
import { ApiRequestError } from "./api";
import { createApiClient, SESION_PERDIDA } from "./api-client";
import { SIGN_IN_PATH } from "./auth-config";

export { PANEL_PATH, SIGN_IN_PATH, SIGN_UP_PATH } from "./auth-config";

/** Lo que la API cuenta de la cuenta. Ningun secreto: eso no sale nunca. */
export interface Account {
  readonly id: string;
  readonly email: string;
  readonly emailVerified: boolean;
  readonly name: string | null;
  readonly imageUrl: string | null;
  readonly hasPassword: boolean;
  readonly totpEnabled: boolean;
  readonly recoveryCodesLeft: number;
  /** Cuantas llaves de acceso tiene (D-078). Opcional: una API vieja no lo manda. */
  readonly passkeys?: number;
  readonly google: { readonly email: string | null } | null;
  readonly createdAt: string;
}

export interface AccountResponse {
  readonly user: Account;
  readonly session: {
    readonly id: string | null;
    readonly mfa: boolean;
    readonly mfaAt: string | null;
    readonly createdAt: string;
  } | null;
}

/** Que formas de ingresar ofrece esta instalacion (`GET /v1/auth/config`). */
export interface AuthOptions {
  readonly google: boolean;
  readonly passwordSignup: boolean;
  readonly passwordReset: boolean;
  readonly configured: boolean;
}

type Estado = "sin-consultar" | "cargando" | "con-sesion" | "sin-sesion" | "error";

interface SessionValue {
  readonly estado: Estado;
  readonly cuenta: AccountResponse | null;
  /** Pide la sesion si todavia no se pidio. Lo llaman las pantallas que la necesitan. */
  readonly asegurar: () => void;
  /** Vuelve a preguntar: despues de ingresar, de cambiar el nombre, de activar el segundo factor. */
  readonly recargar: () => Promise<AccountResponse | null>;
  /** Cierra la sesion en el servidor y olvida la cuenta. */
  readonly salir: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [estado, setEstado] = useState<Estado>("sin-consultar");
  const [cuenta, setCuenta] = useState<AccountResponse | null>(null);
  const api = useMemo(() => createApiClient(), []);

  const recargar = useCallback(async (): Promise<AccountResponse | null> => {
    setEstado((actual) => (actual === "con-sesion" ? actual : "cargando"));
    try {
      const respuesta = await api.get<AccountResponse>("/v1/auth/yo");
      setCuenta(respuesta);
      setEstado("con-sesion");
      return respuesta;
    } catch (causa) {
      setCuenta(null);
      setEstado(causa instanceof ApiRequestError && causa.status === 401 ? "sin-sesion" : "error");
      return null;
    }
  }, [api]);

  const asegurar = useCallback(() => {
    setEstado((actual) => {
      if (actual === "sin-consultar") void recargar();
      return actual === "sin-consultar" ? "cargando" : actual;
    });
  }, [recargar]);

  const salir = useCallback(async () => {
    try {
      await api.post("/v1/auth/salir");
    } finally {
      setCuenta(null);
      setEstado("sin-sesion");
    }
  }, [api]);

  // La API dijo 401 en una ruta privada: la sesion ya no existe.
  useEffect(() => {
    const perdida = () => {
      setCuenta(null);
      setEstado("sin-sesion");
    };
    window.addEventListener(SESION_PERDIDA, perdida);
    return () => window.removeEventListener(SESION_PERDIDA, perdida);
  }, []);

  const valor = useMemo(
    () => ({ estado, cuenta, asegurar, recargar, salir }),
    [estado, cuenta, asegurar, recargar, salir],
  );

  return <SessionContext.Provider value={valor}>{children}</SessionContext.Provider>;
}

/** La sesion. Al usarlo, se pregunta por ella si todavia no se pregunto. */
export function useSession(): SessionValue {
  const valor = useContext(SessionContext);
  if (!valor) throw new Error("useSession necesita SessionProvider arriba.");

  const { estado, asegurar } = valor;
  useEffect(() => {
    if (estado === "sin-consultar") asegurar();
  }, [estado, asegurar]);

  return valor;
}

/** Las opciones de ingreso de la instalacion, una vez por pagina. */
let opcionesEnCurso: Promise<AuthOptions> | null = null;

export function useAuthOptions(): AuthOptions | null {
  const [opciones, setOpciones] = useState<AuthOptions | null>(null);

  useEffect(() => {
    let vigente = true;
    opcionesEnCurso ??= createApiClient()
      .get<AuthOptions>("/v1/auth/config")
      .catch(() => {
        opcionesEnCurso = null;
        // Sin respuesta, se ofrece lo basico: el formulario. Si la API no
        // responde, el formulario lo va a decir al enviarlo.
        return { google: false, passwordSignup: true, passwordReset: true, configured: true };
      });
    void opcionesEnCurso.then((valor) => {
      if (vigente) setOpciones(valor);
    });
    return () => {
      vigente = false;
    };
  }, []);

  return opciones;
}

/**
 * Envuelve las rutas que exigen sesion iniciada. Sin sesion, a ingresar —
 * recordando a donde se queria ir.
 */
export function RequireSession({ children }: { children: ReactNode }): ReactNode {
  const { estado, recargar } = useSession();
  const location = useLocation();

  if (estado === "sin-consultar" || estado === "cargando") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <LoadingState label="Cargando..." />
      </div>
    );
  }

  if (estado === "error") {
    return (
      <main className="mx-auto max-w-lg px-4 py-24 text-center">
        <h1 className="titulo text-xl font-semibold">No pudimos conectarnos</h1>
        <p className="mt-2 text-muted">Revisa tu conexion e intenta de nuevo.</p>
        <button
          type="button"
          className="mt-6 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          onClick={() => void recargar()}
        >
          Reintentar
        </button>
      </main>
    );
  }

  if (estado === "sin-sesion") {
    const volver = `${location.pathname}${location.search}`;
    return <Navigate to={`${SIGN_IN_PATH}?volver=${encodeURIComponent(volver)}`} replace />;
  }

  return <>{children}</>;
}

/** Las iniciales de alguien, para el boton de la cuenta cuando no hay foto. */
export function iniciales(cuenta: Account): string {
  const base = cuenta.name?.trim() || cuenta.email;
  const partes = base.split(/[\s@._-]+/).filter(Boolean);
  return ((partes[0]?.[0] ?? "") + (partes[1]?.[0] ?? "")).toUpperCase() || "?";
}
